import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const COLORS = {
  primary: '#4F46E5',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  muted: '#6B7280',
  bg: '#F9FAFB',
  white: '#FFFFFF',
  border: '#E5E7EB',
};

const STATUS_COLORS = {
  waiting: COLORS.info,
  offered: COLORS.warning,
  fulfilled: COLORS.success,
  expired: COLORS.muted,
  cancelled: COLORS.danger,
  pending: COLORS.warning,
  accepted: COLORS.success,
  declined: COLORS.danger,
  booking_failed: COLORS.danger,
  no_response: COLORS.muted,
};

function WaitlistManagement({ practiceId }) {
  const [activeTab, setActiveTab] = useState('queue');
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [offerMetrics, setOfferMetrics] = useState(null);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [months, setMonths] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filterByDate = (list) => {
    if (!dateFrom && !dateTo) return list;
    return list.filter(e => {
      const d = (e.created_at || '').split('T')[0];
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  };

  // ─── Data Loading ─────────────────────────────────────

  const loadQueue = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const result = await api.get(`/waitlist?practiceId=${practiceId}`);
      if (result.success) {
        setEntries(result.data.entries || []);
        setStats(result.data.stats || null);
      }
    } catch (err) {
      console.error('Error loading waitlist:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  const loadMetrics = useCallback(async () => {
    if (!practiceId) return;
    try {
      const result = await api.get(`/waitlist/metrics?practiceId=${practiceId}&months=${months}`);
      if (result.success) {
        setOfferMetrics(result.data.offers || null);
      }
    } catch (err) {
      console.error('Error loading metrics:', err);
    }
  }, [practiceId, months]);

  const loadPendingOffers = useCallback(async () => {
    if (!practiceId) return;
    try {
      // Load entries with 'offered' status and their offers
      const result = await api.get(`/waitlist?practiceId=${practiceId}&status=offered`);
      if (result.success) {
        setPendingOffers(result.data.entries || []);
      }
    } catch (err) {
      console.error('Error loading pending offers:', err);
    }
  }, [practiceId]);

  useEffect(() => {
    loadQueue();
    loadMetrics();
    loadPendingOffers();
  }, [loadQueue, loadMetrics, loadPendingOffers]);

  // Auto-refresh active offers every 30s
  useEffect(() => {
    if (activeTab !== 'offers') return;
    const interval = setInterval(loadPendingOffers, 30000);
    return () => clearInterval(interval);
  }, [activeTab, loadPendingOffers]);

  // ─── Actions ──────────────────────────────────────────

  const removeEntry = async (id) => {
    if (!window.confirm('Remove this patient from the waitlist?')) return;
    try {
      await api.delete(`/waitlist/${id}?practiceId=${practiceId}`);
      loadQueue();
    } catch (err) {
      alert('Failed to remove: ' + err.message);
    }
  };

  // ─── Render ───────────────────────────────────────────

  if (loading && entries.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: COLORS.muted }}>Loading waitlist...</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700 }}>Waitlist Management</h1>
          <p style={{ margin: '0.25rem 0 0', color: COLORS.muted, fontSize: '0.9rem' }}>
            AI-powered schedule backfill — automatically fill cancelled appointment slots
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            background: COLORS.primary, color: '#fff', border: 'none', padding: '0.6rem 1.2rem',
            borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem'
          }}
        >
          + Add to Waitlist
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <StatCard label="Waiting" value={stats.waiting || entries.filter(e => e.status === 'waiting').length} color={COLORS.info} />
          <StatCard label="Offered" value={stats.offered || entries.filter(e => e.status === 'offered').length} color={COLORS.warning} />
          <StatCard label="Fulfilled" value={stats.fulfilled || entries.filter(e => e.status === 'fulfilled').length} color={COLORS.success} />
          <StatCard label="Fill Rate" value={offerMetrics ? `${offerMetrics.fill_rate}%` : '—'} color={COLORS.primary} />
          <StatCard label="Revenue Recovered" value={offerMetrics ? `$${fmt(offerMetrics.revenue_recovered)}` : '—'} color={COLORS.success} />
        </div>
      )}

      {/* Date Filter */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', fontSize: '0.85rem' }}>
        <label style={{ color: COLORS.muted, fontWeight: 600 }}>Filter by date:</label>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '0.4rem', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: '0.85rem' }} />
        <span style={{ color: COLORS.muted }}>to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '0.4rem', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: '0.85rem' }} />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Clear</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: `2px solid ${COLORS.border}` }}>
        {['queue', 'offers', 'history', 'metrics'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.6rem 1.2rem', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
              background: activeTab === tab ? COLORS.primary : 'transparent',
              color: activeTab === tab ? '#fff' : COLORS.muted,
              borderRadius: '8px 8px 0 0',
            }}
          >
            {{ queue: 'Queue', offers: 'Active Offers', history: 'History', metrics: 'Metrics' }[tab]}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'queue' && <QueueTab entries={filterByDate(entries.filter(e => e.status !== 'fulfilled' && e.status !== 'cancelled'))} onRemove={removeEntry} />}
      {activeTab === 'offers' && <OffersTab entries={pendingOffers} onRefresh={loadPendingOffers} />}
      {activeTab === 'history' && <HistoryTab entries={filterByDate(entries.filter(e => ['fulfilled', 'offered', 'cancelled', 'expired'].includes(e.status)))} />}
      {activeTab === 'metrics' && <MetricsTab metrics={offerMetrics} months={months} setMonths={setMonths} />}

      {/* Add Modal */}
      {showAddModal && (
        <AddModal
          practiceId={practiceId}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); loadQueue(); }}
        />
      )}
    </div>
  );
}

// ─── Queue Tab ────────────────────────────────────────────

function QueueTab({ entries, onRemove }) {
  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: COLORS.muted }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>No patients on waitlist</div>
        <p>Add patients who want earlier appointments or specific time slots.</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: COLORS.bg, borderBottom: `2px solid ${COLORS.border}` }}>
            <th style={thStyle}>Patient</th>
            <th style={thStyle}>Preferred Provider</th>
            <th style={thStyle}>Appt Type</th>
            <th style={thStyle}>Date Range</th>
            <th style={thStyle}>Priority</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Wait Time</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{entry.first_name} {entry.last_name}</div>
                <div style={{ fontSize: '0.8rem', color: COLORS.muted }}>{entry.phone}</div>
              </td>
              <td style={tdStyle}>{entry.preferred_provider_id || 'Any'}</td>
              <td style={tdStyle}>{entry.preferred_appointment_type || 'Any'}</td>
              <td style={tdStyle}>
                {entry.preferred_date_start
                  ? `${fmtDate(entry.preferred_date_start)} — ${fmtDate(entry.preferred_date_end)}`
                  : 'Any'}
              </td>
              <td style={tdStyle}>
                <PriorityBar value={entry.priority || 0.5} />
              </td>
              <td style={tdStyle}>
                <StatusBadge status={entry.status} />
              </td>
              <td style={tdStyle}>{timeAgo(entry.created_at)}</td>
              <td style={tdStyle}>
                <button onClick={() => onRemove(entry.id)} style={btnSmall('#EF4444')}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── History Tab ──────────────────────────────────────────

function HistoryTab({ entries }) {
  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: COLORS.muted }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>No history yet</div>
        <p>Filled, offered, and expired entries will appear here.</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: COLORS.bg, borderBottom: `2px solid ${COLORS.border}` }}>
            <th style={thStyle}>Patient</th>
            <th style={thStyle}>Appt Type</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Added</th>
            <th style={thStyle}>Resolved</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{entry.first_name} {entry.last_name}</div>
                <div style={{ fontSize: '0.8rem', color: COLORS.muted }}>{entry.phone}</div>
              </td>
              <td style={tdStyle}>{entry.preferred_appointment_type || 'Any'}</td>
              <td style={tdStyle}><StatusBadge status={entry.status} /></td>
              <td style={tdStyle}>{fmtDate(entry.created_at)}</td>
              <td style={tdStyle}>{entry.updated_at ? fmtDate(entry.updated_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Active Offers Tab ────────────────────────────────────

function OffersTab({ entries, onRefresh }) {
  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: COLORS.muted }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No active offers</div>
        <p>Offers are sent automatically when a cancellation matches a waitlisted patient.</p>
        <button onClick={onRefresh} style={btnSmall(COLORS.info)}>Refresh</button>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button onClick={onRefresh} style={btnSmall(COLORS.info)}>Refresh</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: COLORS.bg, borderBottom: `2px solid ${COLORS.border}` }}>
            <th style={thStyle}>Patient</th>
            <th style={thStyle}>Offered Slot</th>
            <th style={thStyle}>Channel</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Offered At</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{entry.first_name} {entry.last_name}</div>
              </td>
              <td style={tdStyle}>{entry.preferred_appointment_type || '—'}</td>
              <td style={tdStyle}>SMS</td>
              <td style={tdStyle}><StatusBadge status={entry.status} /></td>
              <td style={tdStyle}>{timeAgo(entry.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Metrics Tab ──────────────────────────────────────────

function MetricsTab({ metrics, months, setMonths }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
        {[1, 3, 6, 12].map(m => (
          <button
            key={m}
            onClick={() => setMonths(m)}
            style={{
              padding: '0.4rem 0.8rem', borderRadius: '6px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.85rem',
              background: months === m ? COLORS.primary : COLORS.bg,
              color: months === m ? '#fff' : COLORS.muted,
            }}
          >
            {m}mo
          </button>
        ))}
      </div>

      {!metrics ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: COLORS.muted }}>No metrics data yet</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <MetricCard label="Total Offers Sent" value={metrics.total_offers || 0} color={COLORS.info} />
          <MetricCard label="Accepted" value={metrics.accepted || 0} color={COLORS.success} />
          <MetricCard label="Declined" value={metrics.declined || 0} color={COLORS.danger} />
          <MetricCard label="Expired" value={metrics.expired || 0} color={COLORS.muted} />
          <MetricCard label="Fill Rate" value={`${metrics.fill_rate || 0}%`} color={COLORS.primary} />
          <MetricCard label="Revenue Recovered" value={`$${fmt(metrics.revenue_recovered || 0)}`} color={COLORS.success} />
          <MetricCard
            label="Avg Response Time"
            value={metrics.avg_response_minutes ? `${Math.round(metrics.avg_response_minutes)} min` : '—'}
            color={COLORS.info}
          />
          <MetricCard label="Pending" value={metrics.pending || 0} color={COLORS.warning} />
        </div>
      )}
    </div>
  );
}

// ─── Add Modal ────────────────────────────────────────────

function AddModal({ practiceId, onClose, onAdded }) {
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [providers, setProviders] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [form, setForm] = useState({
    preferred_appointment_type: '',
    preferred_provider_id: '',
    preferred_date_start: '',
    preferred_date_end: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [provRes, typeRes] = await Promise.all([
          api.get(`/practices/${practiceId}/voice/providers`).catch(() => ({ data: [] })),
          api.get(`/practices/${practiceId}/ehr/appointment-types`).catch(() => ({ data: [] })),
        ]);
        setProviders(provRes.data || []);
        setAppointmentTypes(typeRes.data || []);
      } catch { /* non-blocking */ }
    };
    loadOptions();
  }, [practiceId]);

  const searchPatients = async (q) => {
    if (q.length < 2) { setPatients([]); return; }
    try {
      const result = await api.get(`/patients?practiceId=${practiceId}&search=${encodeURIComponent(q)}&limit=10`);
      if (result.success) setPatients(result.data?.patients || result.data || []);
    } catch { setPatients([]); }
  };

  const handleSubmit = async () => {
    if (!selectedPatient) return;
    setSubmitting(true);
    try {
      await api.post(`/waitlist?practiceId=${practiceId}`, {
        patient_id: selectedPatient.id,
        ...form,
      });
      onAdded();
    } catch (err) {
      alert('Failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', width: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem' }}>Add Patient to Waitlist</h2>

        {/* Patient Search */}
        <label style={labelStyle}>Patient</label>
        {selectedPatient ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: COLORS.bg, borderRadius: '6px', marginBottom: '1rem' }}>
            <span style={{ fontWeight: 600 }}>{selectedPatient.first_name} {selectedPatient.last_name}</span>
            <button onClick={() => { setSelectedPatient(null); setPatientSearch(''); }} style={{ ...btnSmall(COLORS.muted), padding: '0.2rem 0.5rem' }}>Change</button>
          </div>
        ) : (
          <div style={{ marginBottom: '1rem' }}>
            <input
              value={patientSearch}
              onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
              placeholder="Search by name..."
              style={inputStyle}
            />
            {patients.length > 0 && (
              <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '6px', maxHeight: '150px', overflowY: 'auto', marginTop: '0.25rem' }}>
                {patients.map(p => (
                  <div key={p.id} onClick={() => { setSelectedPatient(p); setPatients([]); }}
                    style={{ padding: '0.5rem', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}` }}>
                    {p.first_name} {p.last_name} — {p.phone || p.email || 'No contact'}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <label style={labelStyle}>Preferred Provider</label>
        <select value={form.preferred_provider_id} onChange={e => setForm({ ...form, preferred_provider_id: e.target.value })} style={inputStyle}>
          <option value="">Any Provider</option>
          {providers.map(p => (
            <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
          ))}
        </select>

        <label style={labelStyle}>Preferred Appointment Type</label>
        <select value={form.preferred_appointment_type} onChange={e => setForm({ ...form, preferred_appointment_type: e.target.value })} style={inputStyle}>
          <option value="">Any Type</option>
          {appointmentTypes.map(t => (
            <option key={t.id || t.name} value={t.name}>{t.name}</option>
          ))}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Earliest Date</label>
            <input type="date" value={form.preferred_date_start} onChange={e => setForm({ ...form, preferred_date_start: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Latest Date</label>
            <input type="date" value={form.preferred_date_end} onChange={e => setForm({ ...form, preferred_date_end: e.target.value })} style={inputStyle} />
          </div>
        </div>

        <label style={labelStyle}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnSmall(COLORS.muted), padding: '0.5rem 1rem' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!selectedPatient || submitting}
            style={{ ...btnSmall(COLORS.primary), padding: '0.5rem 1rem', opacity: !selectedPatient || submitting ? 0.5 : 1 }}>
            {submitting ? 'Adding...' : 'Add to Waitlist'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Components ────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${color}`, borderRadius: '8px', padding: '1rem' }}>
      <div style={{ fontSize: '0.8rem', color: COLORS.muted, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '1.25rem', textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '0.85rem', color: COLORS.muted, marginTop: '0.25rem' }}>{label}</div>
    </div>
  );
}

function PriorityBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 70 ? COLORS.success : pct >= 40 ? COLORS.warning : COLORS.danger;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div style={{ width: '60px', height: '8px', background: COLORS.bg, borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '4px' }} />
      </div>
      <span style={{ fontSize: '0.8rem', color: COLORS.muted }}>{pct}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || COLORS.muted;
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem',
      fontWeight: 600, background: `${color}15`, color, textTransform: 'capitalize'
    }}>
      {status}
    </span>
  );
}

// ─── Styles ───────────────────────────────────────────────

const thStyle = { padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem', color: COLORS.muted };
const tdStyle = { padding: '0.75rem' };
const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: COLORS.muted, marginBottom: '0.25rem', marginTop: '0.75rem' };
const inputStyle = { width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${COLORS.border}`, fontSize: '0.9rem', boxSizing: 'border-box' };

function btnSmall(color) {
  return {
    background: color, color: '#fff', border: 'none', padding: '0.3rem 0.7rem',
    borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
  };
}

// ─── Helpers ──────────────────────────────────────────────

function fmt(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr);
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default WaitlistManagement;
